import React, { useState } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { createVariant } from '@/services/abTestService';

interface NewVariantFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const NewVariantForm: React.FC<NewVariantFormProps> = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    headline: '',
    subheadline: '',
    description: '',
    primaryColor: '#3b82f6', // Default blue
  });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate form
      if (!formData.id || !formData.name || !formData.headline) {
        toast({
          title: 'Validation Error',
          description: 'Please fill in all required fields.',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      // Ensure ID is URL-friendly
      const urlFriendlyId = formData.id.toLowerCase().replace(/[^a-z0-9-]/g, '-');
      if (urlFriendlyId !== formData.id) {
        toast({
          title: 'ID Format',
          description: 'ID has been adjusted to be URL-friendly.',
        });
        setFormData(prev => ({ ...prev, id: urlFriendlyId }));
      }

      // Create variant
      const result = await createVariant(formData);
      
      if (result.success) {
        toast({
          title: 'Success',
          description: `New variant "${formData.name}" created successfully.`,
        });
        onSuccess();
        onClose();
      } else {
        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Failed to create variant:', error);
      toast({
        title: 'Error',
        description: 'Failed to create variant. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create New Landing Page Variant</DialogTitle>
          <DialogDescription>
            Create a new landing page variation for A/B testing.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="id">Variant ID (URL path)*</Label>
              <Input 
                id="id" 
                name="id" 
                placeholder="e.g., new-variant" 
                value={formData.id} 
                onChange={handleChange} 
                required 
              />
              <p className="text-xs text-gray-500">
                URL-friendly ID for the variant (e.g., /new-variant)
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="name">Variant Name*</Label>
              <Input 
                id="name" 
                name="name" 
                placeholder="e.g., New Variant" 
                value={formData.name} 
                onChange={handleChange} 
                required 
              />
              <p className="text-xs text-gray-500">
                Display name for the dashboard
              </p>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="headline">Headline*</Label>
            <Input 
              id="headline" 
              name="headline" 
              placeholder="e.g., Boost Your Sales with AI" 
              value={formData.headline} 
              onChange={handleChange} 
              required 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="subheadline">Subheadline</Label>
            <Input 
              id="subheadline" 
              name="subheadline" 
              placeholder="e.g., Automate your outreach and book more meetings" 
              value={formData.subheadline} 
              onChange={handleChange} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea 
              id="description" 
              name="description" 
              placeholder="Enter a description of the variant and its unique selling proposition" 
              value={formData.description} 
              onChange={handleChange} 
              rows={3} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="primaryColor">Primary Color</Label>
            <div className="flex gap-2">
              <Input 
                id="primaryColor" 
                name="primaryColor" 
                type="color" 
                value={formData.primaryColor} 
                onChange={handleChange} 
                className="w-12 h-12 p-1" 
              />
              <Input 
                value={formData.primaryColor} 
                onChange={handleChange} 
                name="primaryColor" 
                className="flex-1" 
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create Variant'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default NewVariantForm; 